import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { findTourTarget } from "@/features/tours/findTarget";
import { placeTourCard } from "@/features/tours/placeTourCard";
import { locationMatchesRoute } from "@/features/tours/routeMatch";
import { useTour } from "@/features/tours/TourProvider";
import "@/features/tours/tour.css";

/**
 * Über Dialogen (Modal-Stack ab 500), unter Autocomplete-Listen (z-1000),
 * damit die Projektauswahl während einer Führung benutzbar bleibt.
 */
const TOUR_Z = 860;
const WAIT_MS = 4000;
const OPTIONAL_WAIT_MS = 1600;

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
  radius: number;
}

const RING_PAD = 0;

function sameBox(a: Box, b: Box): boolean {
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5 &&
    Math.abs(a.radius - b.radius) < 0.5
  );
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Even-odd-Pfad: volle Fläche minus abgerundetes Loch in der Schaltflächenform. */
function holePath(box: Box): string {
  const vw = Math.round(window.innerWidth);
  const vh = Math.round(window.innerHeight);
  const x = round1(box.left);
  const y = round1(box.top);
  const w = round1(box.width);
  const h = round1(box.height);
  const r = round1(Math.max(0, Math.min(box.radius, w / 2, h / 2)));
  const outer = `M0 0H${vw}V${vh}H0Z`;
  const x2 = round1(x + w);
  const y2 = round1(y + h);
  const inner =
    r < 1
      ? `M${x} ${y}H${x2}V${y2}H${x}Z`
      : `M${x + r} ${y}H${x2 - r}A${r} ${r} 0 0 1 ${x2} ${y + r}V${y2 - r}A${r} ${r} 0 0 1 ${x2 - r} ${y2}H${x + r}A${r} ${r} 0 0 1 ${x} ${y2 - r}V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`;
  return `${outer} ${inner}`;
}

function measure(el: HTMLElement): Box {
  const rect = el.getBoundingClientRect();
  let top = rect.top;
  let left = rect.left;
  let width = rect.width;
  let height = rect.height;
  // Große Flächen (Listen, Formularblöcke) nur am Anfang markieren,
  // sonst füllt das Loch den Bildschirm und die Karte liegt darauf.
  if (height > 168) height = 88;
  if (width > 560) width = Math.min(width, 420);
  top = Math.max(RING_PAD, top - RING_PAD);
  left = Math.max(RING_PAD, left - RING_PAD);
  const right = Math.min(window.innerWidth - RING_PAD, left + width + RING_PAD * 2);
  const bottom = Math.min(window.innerHeight - RING_PAD, top + height + RING_PAD * 2);
  width = Math.max(24, right - left);
  height = Math.max(24, bottom - top);
  const raw = parseFloat(getComputedStyle(el).borderTopLeftRadius);
  const base = Number.isFinite(raw) ? raw : 8;
  const radius = Math.min(Math.min(width, height) / 2, base + RING_PAD);
  return { top, left, width, height, radius };
}

export function TourOverlay() {
  const { active, next, prev, stop } = useTour();
  const location = useLocation();
  const navigate = useNavigate();
  const step = active?.steps[active.index] ?? null;
  const [box, setBox] = useState<Box | null>(null);
  const [missingStepId, setMissingStepId] = useState<string | null>(null);
  const [cardHeight, setCardHeight] = useState(188);
  const cardRef = useRef<HTMLDivElement>(null);
  const autoSkipped = useRef(new Set<string>());

  const routeReady = locationMatchesRoute(location.pathname, location.search, step?.route ?? null);

  useEffect(() => {
    if (!step?.route || routeReady) return;
    navigate(step.route);
  }, [navigate, routeReady, step]);

  useEffect(() => {
    if (!step || !routeReady) {
      setBox(null);
      setMissingStepId(null);
      return;
    }
    let cancelled = false;
    const started = performance.now();
    const wait = step.optional ? OPTIONAL_WAIT_MS : WAIT_MS;
    const stepId = step.id;

    const tick = () => {
      if (cancelled) return;
      const el = findTourTarget(step.target);
      if (el) {
        const rect = el.getBoundingClientRect();
        const inView = rect.top >= 8 && rect.bottom <= window.innerHeight - 8 && rect.left >= 0 && rect.right <= window.innerWidth;
        if (!inView) el.scrollIntoView({ block: "nearest", inline: "nearest" });
        setBox(measure(el));
        setMissingStepId(null);
        return;
      }
      if (performance.now() - started >= wait) {
        setBox(null);
        setMissingStepId(stepId);
        return;
      }
      window.setTimeout(tick, 120);
    };

    setMissingStepId(null);
    const timer = window.setTimeout(tick, 60);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [routeReady, step, location.pathname, location.search]);

  const missing = missingStepId != null && missingStepId === step?.id;

  useEffect(() => {
    autoSkipped.current.clear();
  }, [active?.tourId]);

  useEffect(() => {
    if (!missing || !step?.optional) return;
    if (autoSkipped.current.has(step.id)) return;
    autoSkipped.current.add(step.id);
    next();
  }, [missing, next, step]);

  useEffect(() => {
    if (!step || !routeReady) return;
    let frame = 0;
    const follow = () => {
      const el = findTourTarget(step.target);
      if (el) {
        const measured = measure(el);
        setBox((prev) => (prev && sameBox(prev, measured) ? prev : measured));
      }
      frame = window.requestAnimationFrame(follow);
    };
    frame = window.requestAnimationFrame(follow);
    return () => window.cancelAnimationFrame(frame);
  }, [routeReady, step]);

  useEffect(() => {
    if (!step || step.advance !== "click") return;
    const onClick = (event: MouseEvent) => {
      const node = event.target;
      if (!(node instanceof Node)) return;
      const el = findTourTarget(step.target);
      if (!el || !el.contains(node)) return;
      window.setTimeout(() => next(), 0);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [next, step]);

  useLayoutEffect(() => {
    const node = cardRef.current;
    if (!node) return;
    const next = node.offsetHeight;
    if (next > 0 && Math.abs(next - cardHeight) > 1) setCardHeight(next);
  }, [cardHeight, step?.id, step?.title, step?.body, missing]);

  if (!active || !step) return null;

  const last = active.index === active.steps.length - 1;
  const showCard = missing && !step.optional;
  const progress = ((active.index + 1) / active.steps.length) * 100;
  const placement = box
    ? placeTourCard(box, cardHeight, { width: window.innerWidth, height: window.innerHeight })
    : {
        top: Math.max(16, window.innerHeight - cardHeight - 16),
        left: 16,
        width: Math.min(340, window.innerWidth - 32),
        side: "bottom" as const,
      };

  return createPortal(
    <div className="eh-tour" style={{ zIndex: TOUR_Z }}>
      <div
        className="eh-tour__shade"
        style={box ? { clipPath: `path(evenodd, "${holePath(box)}")` } : undefined}
      />
      {box ? (
        <div
          className="eh-tour__ring"
          style={{
            top: box.top,
            left: box.left,
            width: box.width,
            height: box.height,
            borderRadius: box.radius,
          }}
        />
      ) : null}

      <div
        ref={cardRef}
        className="eh-tour__card rounded-2xl bg-card text-label shadow-ios-md ring-1 ring-separator/60"
        role="dialog"
        aria-modal="false"
        aria-labelledby="eh-tour-title"
        data-side={box ? placement.side : undefined}
        style={{
          top: placement.top,
          left: placement.left,
          width: placement.width,
          opacity: box || showCard ? 1 : 0,
          ["--eh-tour-caret" as string]: box
            ? `${Math.min(
                Math.max(
                  22,
                  (placement.side === "left" || placement.side === "right"
                    ? box.top + box.height / 2 - placement.top
                    : box.left + box.width / 2 - placement.left) - 6,
                ),
                (placement.side === "left" || placement.side === "right" ? cardHeight : placement.width) - 28,
              )}px`
            : undefined,
        }}
      >
        {box ? <span className={`eh-tour__caret eh-tour__caret--${placement.side}`} aria-hidden /> : null}
        <div className="eh-tour__progress" aria-hidden>
          <span style={{ width: `${progress}%` }} />
        </div>
        <div key={step.id} className="eh-tour__body">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">
            {active.title}
            <span className="mx-1.5 text-separator">·</span>
            {active.index + 1} von {active.steps.length}
          </p>
          <h2 id="eh-tour-title" className="mt-1.5 text-base font-semibold leading-snug text-label">
            {showCard ? "Dieser Schritt ist gerade nicht zu sehen" : step.title}
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-label-secondary">
            {showCard
              ? "Die Schaltfläche fehlt auf dieser Seite, zum Beispiel weil sie für deine Rolle nicht da ist oder die Maske noch lädt."
              : step.body}
          </p>
          {step.advance === "click" && !showCard ? (
            <p className="mt-2 text-xs font-medium text-accent">Klicke die markierte Schaltfläche.</p>
          ) : null}
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            {active.index > 0 ? (
              <button type="button" className="eh-tour__btn eh-tour__btn--ghost" onClick={prev}>
                Zurück
              </button>
            ) : null}
            <button type="button" className="eh-tour__btn eh-tour__btn--ghost" onClick={stop}>
              Beenden
            </button>
            {step.advance === "click" && !last ? (
              <button type="button" className="eh-tour__btn eh-tour__btn--ghost" onClick={next}>
                Überspringen
              </button>
            ) : null}
            {step.advance === "next" || showCard || (step.advance === "click" && last) ? (
              <button type="button" className="eh-tour__btn eh-tour__btn--primary" onClick={next}>
                {last ? "Fertig" : "Weiter"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
